import React, { useContext, useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Collapse,
  TextTitle,
  useCollapseState,
  collapseExpander,
  ValidatedPanel,
  getPanelErrorProps
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import { AssetGroup, Campaign } from '@bingads-webui/campaign-odata-schemas';
import { StoreSelectContainer } from '@bingads-webui-campaign-react/store-select';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ShoppingImg, ListingGroupImg, apiErrorMapper } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { getStoreTitleDetails, getListingGroupDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { ValidatedListingGroups } from '@bingads-webui-campaign-react/step-wizard-controls';
import { withScope, QueryParam, ScopeContext } from '@bingads-webui-react/scope-service';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { legacyI18nOfListingGroup } from '@bingads-webui-campaign-react/listing-groups-page';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { StoreSelectorShimmer, SingleLineShimmer } from '../../shared/components/section-shimmer';

const getProductStepStyles = () => ({
  container: {
    maxWidth: 680,
  },
  header: {
    marginBottom: 20,
  },
});

const getProductSettingStyles = () => ({
  width: '100%',
  '& .text-assets-collection-container': {
    width: 'auto',
  },
  '& .asset-group-name-section .error-string-wrapper': {
    right: 'unset !important',
    position: 'unset !important',
  },

  '& .text-input-container': {
    width: '100%',
  },
  '& .date-settings table': {
    width: 'unset !important',
  },
  '& .asset-group-enhance-ads-section': {
    '& .section-description': {
      marginBottom: '16px',
    },
  },
  'formContainer-xxl': {
    width: '530px',
  },
  'formContainer-xl': {
    width: '430px',
  },
});

const ProductsStepShellInternal = ({
  data = {},
  onDataChange,
  error,
  isSettingPage,
}) => {
  const isLoading = useMemo(() => _.isEmpty(data), [data]);

  const i18n = useContext(I18nContext);

  const getStyles = useResponsiveStyles(getProductSettingStyles);
  const classes = useDefaultStyles(getProductStepStyles);

  const { scope } = useContext(ScopeContext) || {};

  const { state, send } = useWizardService();

  const { campaignObjective } = state.context.wizardData;

  const { saveFailed } = state.context;

  const [storesData, setStoresData] = useState(null);

  const { serverErrors, systemErrors } = error || {};

  const {
    campaign = {},
    assetGroup = {},
  } = data || {};

  const {
    listingGroup,
    productFilter,
  } = assetGroup;

  const {
    Id,
    ProviderId,
    IsFeedFiltered,
    SalesCountry,
    FeedLabel,
    LocalInventoryAdsEnabled,
  } = campaign || {};

  const storeContainerData = useMemo(() => ({
    ProviderId,
    IsFeedFiltered,
    SalesCountry,
    FeedLabel,
    LocalInventoryAdsEnabled,
  }), [ProviderId, IsFeedFiltered, SalesCountry, FeedLabel, LocalInventoryAdsEnabled]);

  const storeTitleDetails = useMemo(() => storesData && getStoreTitleDetails(i18n, storesData, storeContainerData), [i18n, storesData, storeContainerData]);

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap, legacyI18nOfListingGroup]), [i18n]);

  const mergedI18n = useMemo(() => mergeI18n(
    legacyI18n,
    i18n
  ), [i18n, legacyI18n]);

  const [
    allProductsSections,
    productsSectionsExpandedSections,
    setProdutsSectionExpandedSections,
  ] = useCollapseState(
    ['shopping-ads'] /* all sections */,
    ['shopping-ads'] /* initially expanded sections */
  );

  const [
    allListingGroupSections,
    lisitngGroupSectionsExpandedSections,
    setListingGroupExpandedSections,
  ] = useCollapseState(
    ['listing-groups'] /* all sections */,
    ['listing-groups'] /* initially expanded sections */
  );

  const basicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setProdutsSectionExpandedSections }),
    [setProdutsSectionExpandedSections]
  );

  const listingGroupDetails = useMemo(() => getListingGroupDetails(
    i18n,
    !!assetGroup.Id,
    {
      productFilter,
      listingGroup,
    }
  ), [assetGroup.Id, i18n, listingGroup, productFilter]);

  const onStoreDataLoaded = useCallback(({ error: apiError, stores = {}, countries = {} }) => {
    setStoresData({ apiError, stores, countries });
  }, []);

  useEffect(() => {
    if (storesData) {
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { loadedStoresData: storesData });
    }
  }, [send, storesData]);

  const shoppingAdsPanel = (
    <ValidatedPanel
      key="shopping-ads"
      title={<TextTitle titleKey={_TL_('Shopping settings')} details={storeTitleDetails} icon={ShoppingImg} />}
      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      actuationProps={{
        expand: basicSettingsExpand,
        expandKey: 'shopping-ads',
      }}
    >
      {
        isLoading ? <StoreSelectorShimmer /> : (
          <StoreSelectContainer
            campaign={{
              data: campaign,
            }}
            campaignObjective={campaignObjective}
            data={storeContainerData}
            onStoreDataLoaded={onStoreDataLoaded}
            onRenderCustomLoadingComponent={() => <StoreSelectorShimmer />}
          />
        )
      }
    </ValidatedPanel>
  );

  const onNavigateToListingGroups = useCallback(() => {
    const queryParam = new QueryParam(scope.history.location, scope.history);
    queryParam.append({ campaignId: Id, assetGroupId: assetGroup.Id });
    scope.history.push({
      pathname: '/campaign/vnext/listinggroups/list',
      search: queryParam.stringify(),
    });
  }, [Id, assetGroup.Id, scope.history]);

  const listingGroupsSection = useMemo(() => (
    <ValidatedPanel
      key="listing-groups"
      title={<TextTitle
        titleKey={_TL_('Listing groups')}
        details={listingGroupDetails}
        icon={ListingGroupImg}
      />}
      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      actuationProps={{
            expand: basicSettingsExpand,
            expandKey: 'listing-groups',
          }}
    >
      {
        isLoading ? <SingleLineShimmer /> : (
          <ValidatedListingGroups
            i18n={mergedI18n}
            validationProps={{}}
            campaignId={Id}
            campaignSettings={storeContainerData}
            listingGroup={listingGroup}
            providerId={ProviderId}
            assetGroupId={assetGroup.Id}
            onClickNavigateToListingGroups={onNavigateToListingGroups}
          />
        )
      }
    </ValidatedPanel>
  ), [Id, ProviderId, assetGroup.Id, basicSettingsExpand, isLoading, listingGroup, listingGroupDetails, mergedI18n, onNavigateToListingGroups, storeContainerData]);

  const onAssetGroupSettingsValidate = useCallback((result) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroup: {
          ...prevData.assetGroup,
          ...result.data,
        },
      }),
      isValid: result.isValid,
      isDirty: result.isDirty,
    });
  }, [onDataChange]);

  const onCampaignSettingsValidate = useCallback((result) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        campaign: {
          ...prevData.campaign,
          CampaignSettings: {
            ...prevData.campaign.CampaignSettings,
            ...storeContainerData,
          },
          ...result.data,
        },
      }),
      isValid: result.isValid,
      isDirty: result.isDirty,
    });
  }, [onDataChange, storeContainerData]);

  return (
    <FormErrorGroup formErrorProps={{ errors: serverErrors, saveFailed, errorMapper: apiErrorMapper }}>
      <div className={classNames(classes.container, 'products-step-shell')}>
        {!isSettingPage &&
        <div className={classNames(classes.header, 'page-header-2')}>
          <h1> { i18n.getString(_TL_('Add your products')) } </h1>
          <div className="page-description">
            <span>{i18n.getString(_TL_('Choose which products to show in your ads. Some of your ads will use images, headlines and descriptions from Merchant center'))}</span>
          </div>
        </div>
        }
        <>
          {!isSettingPage &&
          <>
            <UnmappedFormErrors className="other-server-errors" />
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            <ValidationGroup
              validationProps={{
              schema: Campaign,
              onValidate: onCampaignSettingsValidate,
              data: campaign,
              i18n,
            }}
            >
              <Collapse
                allPanelItems={allProductsSections}
                checkItems
                skipItemsTypeCheck
                activeKeys={productsSectionsExpandedSections}
                setActiveKeys={setProdutsSectionExpandedSections}
              >
                {shoppingAdsPanel}
              </Collapse>
            </ValidationGroup>
            <ValidationGroup
              validationProps={{
              schema: AssetGroup,
              onValidate: onAssetGroupSettingsValidate,
              data: data.assetGroup,
              i18n,
              stopPropogation: true,
            }}
            >
              <Collapse
                allPanelItems={allListingGroupSections}
                checkItems
                skipItemsTypeCheck
                activeKeys={lisitngGroupSectionsExpandedSections}
                setActiveKeys={setListingGroupExpandedSections}
              >
                {listingGroupsSection}
              </Collapse>
            </ValidationGroup>
          </>
          }
          {isSettingPage &&
          <div className={classNames(getStyles('formContainer'), 'controls-affecting-preview-container')}>
            <ValidationGroup
              validationProps={{
              schema: AssetGroup,
              onValidate: onAssetGroupSettingsValidate,
              data: data.assetGroup,
              i18n,
              stopPropogation: true,
            }}
            >
              <Collapse
                allPanelItems={allListingGroupSections}
                checkItems
                skipItemsTypeCheck
                activeKeys={lisitngGroupSectionsExpandedSections}
                setActiveKeys={setListingGroupExpandedSections}
              >
                {listingGroupsSection}
              </Collapse>
            </ValidationGroup>
          </div>
}
        </>
      </div>
    </FormErrorGroup>
  );
};

ProductsStepShellInternal.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({
      Id: PropTypes.number,
    }),
    assetGroup: PropTypes.shape({
      Id: PropTypes.number,
    }),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.string),
    systemErrors: PropTypes.arrayOf(PropTypes.string),
  }),
  scope: PropTypes.shape({
    values: PropTypes.shape({
      assetGroupId: PropTypes.number,
    }),
  }).isRequired,
  isSettingPage: PropTypes.bool,
};

ProductsStepShellInternal.defaultProps = {
  error: {},
  isSettingPage: false,
};

export const ProductsStepShell = withScope(ProductsStepShellInternal);
